import React from 'react';
import {
  CoverageStatus,
  JurisdictionDisposition,
  JurisdictionRouting,
} from '../../interfaces/iStreamedResponse';

/**
 * Shared presentation of the v2 conditional-routing jurisdiction result
 * (EXPORT_JURISDICTION_ROUTING_V2). ONE component owns the visual language so every
 * surface — the live final result (ExportResultsComp), the per-regime cards
 * (NRCResults / ITARResults), and later saved work (ConsolidatedResults) + PDF —
 * reads identically. See the conditional-routing plan §5.8 / §6 and Slice 6.
 *
 * Novice-safety requirement (§5.8): the three "continued" states must be visually
 * distinct from a confident STOP, and "continued with caveats" must NOT read as
 * "cleared". Only `stopped` means "review was not performed because exclusive
 * jurisdiction was confirmed"; the two `continued_*` states mean the review KEPT
 * GOING because a control was flagged or was unresolved.
 *
 * Everything here self-hides when the data is absent (OpenAI rollback path / older
 * saved runs / flag OFF), so nothing new renders unless v2 produced a disposition.
 */

// --- inline icons (inherit the badge/warning text color via currentColor) --------------
const shieldCheckIcon = (
  <svg width='13' height='13' viewBox='0 0 16 16' fill='none' aria-hidden='true'>
    <path
      d='M8 1.5L2.75 3.75V7.5C2.75 10.75 4.9 13 8 14.5C11.1 13 13.25 10.75 13.25 7.5V3.75L8 1.5Z'
      stroke='currentColor'
      strokeWidth='1.4'
      strokeLinejoin='round'
    />
    <path
      d='M5.75 8L7.25 9.5L10.5 6.25'
      stroke='currentColor'
      strokeWidth='1.4'
      strokeLinecap='round'
      strokeLinejoin='round'
    />
  </svg>
);
const infoIcon = (
  <svg width='13' height='13' viewBox='0 0 16 16' fill='none' aria-hidden='true'>
    <circle cx='8' cy='8' r='6.5' stroke='currentColor' strokeWidth='1.4' />
    <path
      d='M8 7.5V11M8 4.75H8.007'
      stroke='currentColor'
      strokeWidth='1.4'
      strokeLinecap='round'
      strokeLinejoin='round'
    />
  </svg>
);
export const alertTriangleIcon = (
  <svg width='13' height='13' viewBox='0 0 16 16' fill='none' aria-hidden='true'>
    <path
      d='M8 2L14.5 13.5H1.5L8 2Z'
      stroke='currentColor'
      strokeWidth='1.4'
      strokeLinejoin='round'
    />
    <path
      d='M8 6.5V9M8 11.25H8.007'
      stroke='currentColor'
      strokeWidth='1.4'
      strokeLinecap='round'
      strokeLinejoin='round'
    />
  </svg>
);
const checkCircleIcon = (
  <svg width='13' height='13' viewBox='0 0 16 16' fill='none' aria-hidden='true'>
    <circle cx='8' cy='8' r='6.5' stroke='currentColor' strokeWidth='1.4' />
    <path
      d='M5.25 8L7.25 10L10.75 6'
      stroke='currentColor'
      strokeWidth='1.4'
      strokeLinecap='round'
      strokeLinejoin='round'
    />
  </svg>
);

// --- package-level disposition (data.jurisdiction_routing.disposition) ------------------
interface DispositionMeta {
  className: string; // the color modifier on .jd-badge
  label: string; // the pill wording — never reads as "cleared" for a continued state
  icon: React.ReactNode;
  // when true the prominent manual-review caution box is shown for this state
  caution: boolean;
}

const DISPOSITION_META: Record<JurisdictionDisposition, DispositionMeta> = {
  stopped: {
    className: 'jd-badge--stopped',
    label: 'Exclusive jurisdiction confirmed',
    icon: shieldCheckIcon,
    caution: false,
  },
  continued_multi_regime: {
    className: 'jd-badge--multi',
    label: 'Control flagged — review continued',
    icon: infoIcon,
    caution: false,
  },
  continued_unresolved: {
    className: 'jd-badge--unresolved',
    label: 'Jurisdiction unresolved — review continued',
    icon: alertTriangleIcon,
    caution: true,
  },
  clean: {
    className: 'jd-badge--clean',
    label: 'No jurisdiction conflict',
    icon: checkCircleIcon,
    caution: false,
  },
};

/** Normalize an incoming disposition value to a known key, or null. */
export function normalizeDisposition(
  disposition?: string | null,
): JurisdictionDisposition | null {
  const s = String(disposition || '').trim().toLowerCase();
  return s in DISPOSITION_META ? (s as JurisdictionDisposition) : null;
}

/** The pill label for a disposition (the ONE shared wording; reused by the PDF). Empty when unknown. */
export function dispositionLabel(disposition?: string | null): string {
  const key = normalizeDisposition(disposition);
  return key ? DISPOSITION_META[key].label : '';
}

/** Whether a routing result should carry the prominent manual-review caution. */
export function jurisdictionNeedsManualReview(
  routing?: JurisdictionRouting | null,
): boolean {
  if (!routing) return false;
  const key = normalizeDisposition(routing.disposition);
  const cautionState = key ? DISPOSITION_META[key].caution : false;
  const hasUnresolved =
    Array.isArray(routing.unresolved_regimes) && routing.unresolved_regimes.length > 0;
  return cautionState || hasUnresolved;
}

interface BadgeProps {
  routing?: JurisdictionRouting | null;
  /** `pill` renders only the coloured pill (compact — for a card header / row). */
  variant?: 'full' | 'pill';
}

/**
 * The PACKAGE-level jurisdiction disposition: a colour-coded pill, the plain-language
 * reasons the backend already produced, and — for an unresolved package — a prominent
 * manual-review caution. Self-hides when there is no disposition.
 */
const JurisdictionDispositionBadge: React.FC<BadgeProps> = ({
  routing,
  variant = 'full',
}) => {
  const key = normalizeDisposition(routing?.disposition);
  if (!key) return null;

  const meta = DISPOSITION_META[key];
  const reasons = Array.isArray(routing?.reasons)
    ? (routing!.reasons as string[]).filter((r) => r && String(r).trim())
    : [];
  // A multi-regime package that also has unresolved regimes carries the caution too.
  const showCaution = jurisdictionNeedsManualReview(routing);

  const pill = (
    <span className={`jd-badge ${meta.className}`}>
      {meta.icon}
      <span>{meta.label}</span>
    </span>
  );

  if (variant === 'pill') return pill;

  return (
    <div className='jd-disposition'>
      {pill}
      {reasons.length > 0 && (
        <ul className='jd-reasons'>
          {reasons.map((r, i) => (
            <li key={i}>{r}</li>
          ))}
        </ul>
      )}
      {showCaution && (
        <div className='jd-warning' role='note'>
          <span className='jd-warning-icon'>{alertTriangleIcon}</span>
          <span>
            Manual jurisdiction review is recommended. The automated result for the
            unresolved item is provisional — do not rely on it until the jurisdiction
            question is confirmed by qualified review.
          </span>
        </div>
      )}
    </div>
  );
};

// --- per-regime coverage verdict (data.nrc / data.itar .coverage_status) ----------------
interface CoverageMeta {
  className: string;
  label: string;
  plain: string; // plain-language "what this means + did review continue or stop"
}

const COVERAGE_META: Record<CoverageStatus, CoverageMeta> = {
  exclusive: {
    className: 'jd-chip--exclusive',
    label: 'Exclusive control',
    // Regime-blind fallback only — callers pass the regime + routing so the line says which later
    // review was (or was not) skipped; see exclusivePlain below.
    plain: 'This regime was found to exclusively control the item.',
  },
  partial: {
    className: 'jd-chip--partial',
    label: 'Partial coverage',
    plain:
      'This regime covers only part of the package; the remaining elements still require review, so the workflow continued.',
  },
  not_exclusive: {
    className: 'jd-chip--neutral',
    label: 'Not exclusive',
    plain:
      'A relevant match exists, but exclusive control was not established — the workflow continued the remaining reviews.',
  },
  not_applicable: {
    className: 'jd-chip--neutral',
    label: 'Not applicable',
    plain: 'This regime does not cover the item on the available facts.',
  },
  unresolved: {
    className: 'jd-chip--unresolved',
    label: 'Unresolved',
    plain:
      'Jurisdiction is unresolved; later screening continued so a possible control is not hidden. Obtain qualified review before relying on the result.',
  },
};

/** Normalize an incoming coverage value to a known key, or null. */
export function normalizeCoverage(status?: string | null): CoverageStatus | null {
  const s = String(status || '').trim().toLowerCase();
  return (s in COVERAGE_META ? (s as CoverageStatus) : null);
}

/** Which card a coverage verdict belongs to: the nuclear verdict spans NRC + DOE. */
export type CoverageRegime = 'nuclear' | 'itar';

const EXCLUSIVE_COPY: Record<
  CoverageRegime,
  { regime: string; regimeVerb: string; later: string; laterVerb: string; routingKeys: string[] }
> = {
  nuclear: {
    regime: 'The nuclear rules (NRC / DOE)',
    regimeVerb: 'control',
    later: 'the later ITAR and EAR / CCL reviews',
    laterVerb: 'were',
    routingKeys: ['nrc', 'doe'],
  },
  itar: {
    regime: 'ITAR / USML',
    regimeVerb: 'controls',
    later: 'the later EAR / CCL review',
    laterVerb: 'was',
    routingKeys: ['itar'],
  },
};

/** The "exclusive" line, worded for its regime and for what the workflow actually did (bug 4,
 *  2026-10-06): the model's verdict alone does not stop the review — the router's evidence guard
 *  decides — so a refused STOP must not claim the later review was skipped. */
function exclusivePlain(
  regime: CoverageRegime,
  routing?: Partial<JurisdictionRouting> | null,
): string {
  const copy = EXCLUSIVE_COPY[regime];
  const appears = regime === 'nuclear' ? 'appear' : 'appears';
  if (!routing || !routing.disposition) {
    return `${copy.regime} ${regime === 'nuclear' ? 'were' : 'was'} found to exclusively control this item.`;
  }
  const exclusiveRegimes: string[] = Array.isArray(routing.exclusive_regimes)
    ? routing.exclusive_regimes
    : [];
  const stoppedHere =
    routing.disposition === 'stopped' &&
    copy.routingKeys.some((key) => exclusiveRegimes.includes(key));
  if (stoppedHere) {
    return `${copy.regime} exclusively ${copy.regimeVerb} this item, so ${copy.later} ${copy.laterVerb} not performed.`;
  }
  return `${copy.regime} ${appears} to exclusively control this item, but that was not fully confirmed, so it did not stop ${copy.later}. See Jurisdiction Routing for what is still open.`;
}

/** Plain-language explanation of a coverage verdict (for a card's "why" line). Empty when unknown.
 *  Pass the card's `regime` and the run's `jurisdiction_routing` so an "exclusive" verdict says
 *  which later review it stopped — or that it did not stop it. */
export function coverageStatusPlain(
  status?: string | null,
  regime?: CoverageRegime,
  routing?: Partial<JurisdictionRouting> | null,
): string {
  const key = normalizeCoverage(status);
  if (!key) return '';
  if (key === 'exclusive' && regime) return exclusivePlain(regime, routing);
  return COVERAGE_META[key].plain;
}

/** Short label for a coverage verdict (the chip text; reused by the PDF). Empty when unknown. */
export function coverageStatusLabel(status?: string | null): string {
  const key = normalizeCoverage(status);
  return key ? COVERAGE_META[key].label : '';
}

interface ChipProps {
  status?: string | null;
}

/** A small pill for one regime's coverage verdict. Self-hides when the status is absent/unknown. */
export const CoverageStatusChip: React.FC<ChipProps> = ({ status }) => {
  const key = normalizeCoverage(status);
  if (!key) return null;
  const meta = COVERAGE_META[key];
  return <span className={`jd-chip ${meta.className}`}>{meta.label}</span>;
};

export default JurisdictionDispositionBadge;
